<template>
  <div class="document-container">
    <div class="document">
      <div class="header">
        <es-header>
          <template #header>
            <div class="header-title">{{title}}</div>
          </template>
          <template #actions>
            <ul>
              <li class="p-narrow no-border">
                <div class="list-content">
                  <router-link :to="{path: '/personId/reports'}" title="Return to reports list" ref="returnLink">
                    Return to reports list
                  </router-link>
                </div>
              </li>
            </ul>
          </template>
        </es-header>
      </div>
      <div class="content">
        <es-loader v-if="initializing"></es-loader>
        <div class="wrapper" v-else>
          <!-- Person Type, Batch ID and Process Date Range -->
          <div class="columns is-3-desktop multi-dropdown-columns">
            <div class="column is-3">
              <es-select
                :label="`Person Type :`"
                :multi="!contextMode"
                :clearOut="true"
                :preSelected="personTypeCodes"
                :setValues="activePersonTypes"
                v-on:update:values="personTypesChanged($event)"
                ref="selPersonTypes"
                :valueTplFn="(result) => result.name"
                keyValue="code"
                :error-msg="msgPersonType"
                :has-error="hasPersonTypeError"
              />
            </div>

            <div class="column is-3" v-if="hasBatchId">
              <div class="field">
                <label class="label has-text-weight-bold" for="formBatchIdControl">Batch ID :</label>
                <div class="control">
                  <input
                      id="formBatchIdControl"
                      class="input"
                      type="text"
                      v-model="batchId"
                      maxlength="15"
                      :class="{ 'is-danger' : hasErrorBatchId }"
                      @input.prevent="validateField('batchId')">
                </div>
                <div class="is-flex">
                  <span v-if ="hasErrorBatchId" class="help is-danger">
                    {{ msgBatchId }}
                  </span>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field">
                <label class="label has-text-weight-bold" for="formDateRangeControl">Process Date Range :</label>
                <div class="field-body">
                  <div class="field">
                    <div class="control is-flex" style="width: 280px;">
                      <input
                        id="formDateRangeControl"
                        title="Start Date"
                        class="input"
                        :class="{
                          'placeholder': !startDateInput,
                          'is-danger' : hasErrorStartDate
                        }"
                        type="date"
                        v-model="startDateInput"
                        @change.prevent="validateField('startDate')"
                      >
                      <span
                        class="is-flex flex-align--center"
                        style="padding: 0 0.5rem; font-size: 12px"
                      >
                        to
                      </span>
                      <input
                        title="End Date"
                        class="input calendar"
                        :class="{
                          'placeholder': !endDateInput,
                          'is-danger' : hasErrorEndDate
                        }"
                        type="date"
                        v-model="endDateInput"
                        @change.prevent="validateField('endDate')"
                      >
                    </div>
                    <div class="is-flex">
                      <span v-if ="hasErrorStartDate" class="help is-danger">
                        {{ msgStartDate }}
                      </span>
                      <span v-if ="hasErrorEndDate" class="help is-danger"
                        style="padding-left: 11rem; position: absolute">
                        {{ msgEndDate }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- District, School -->
          <div class="columns is-3-desktop multi-dropdown-columns">
            <div class="column is-3">
              <es-select
                :label="`${labels.district} :`"
                :multi="true"
                :search="true"
                api="districts"
                :apiQueryParams="districtsApiQueryParams"
                v-on:update:values="districtsChanged($event)"
                :preSelected="districtCodes"
                ref="selDistricts"
                :valueTplFn="(result) => `${result.location.name} (${result.code})`"
                searchMinLength="2"
                keyValue="code"
              />
            </div>

            <div class="column is-3">
              <es-select
                :label="`${labels.school} :`"
                :multi="true"
                :search="true"
                api="schools"
                :apiQueryParams="getDistrictParams"
                v-on:update:values="schoolsChanged($event)"
                ref="selSchools"
                :preSelected="schoolCodesPromises"
                :valueTplFn="(result) => `${result.location.name} (${result.code})`"
                :subValueTplFn="(result) =>
                  `${result.districtLocation.name} (<i>${result.districtCode }</i>)`"
                searchMinLength="2"
                :key-value="['districtCode', 'code']"
              />
            </div>

            <div class="column is-3" v-if="hasStatus">
              <es-select
                :clear-out="true"
                keyValue="id"
                label="Status :"
                :multi="true"
                :pre-selected="statuses"
                ref="selStatuses"
                :set-values="cancelStatuses"
                value-tpl="${description}"
                @update:values="statuses = $event.map(d => d.id)"
              />
            </div>
          </div>
          <div class="es-actions">
            <es-form-actions>
              <template #form-actions>
                <ul>
                  <li class="reset">
                    <div class="list-content">
                      <a @click.prevent="reset"
                          @keypress.prevent="reset"
                          tabindex="0"
                          role="button">
                        Reset
                      </a>
                    </div>
                  </li>
                  <li>
                    <div class="list-content">
                      <button
                        class="button is-primary"
                        :disabled="loading || initializing"
                        @click="search"
                        >
                          Customize
                      </button>
                    </div>
                  </li>
                </ul>
              </template>
            </es-form-actions>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// components
import EsHeader from '@/components/EsHeader.vue';
import EsSelect from '@/components/EsSelect.vue';
import EsFormActions from '@/components/EsFormActions.vue';
import EsLoader from '@/components/EsLoader.vue';

// mixins
import NotificationManager from '@/mixins/NotificationManager';
import DateUtils from '@/mixins/DateUtils';
import FieldLabels from '@/mixins/FieldLabels';

// store
import { mapGetters } from 'vuex';

// utils
import moment from 'moment';
import axios from 'axios';

export default {
  model: {
    prop: 'searchCriteria',
    event: 'searchCriteriaChanged',
  },
  components: {
    EsHeader,
    EsSelect,
    EsFormActions,
    EsLoader,
  },
  mixins: [NotificationManager, DateUtils, FieldLabels],
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    defaultPersonType: {
      type: String,
      default: '',
    },
    activePersonTypes: {
      type: Array,
      default: () => [],
    },
    searchCriteria: {
      type: Object,
      default: () => ({}),
    },
    title: {
      type: String,
      default: '',
    },
    hasStatus: {
      type: Boolean,
      default: false,
    },
    hasBatchId: {
      type: Boolean,
      default: true,
    },
    statusCode: {
      type: String,
      default: '',
    },
  },
  async created() {
    if (this.hasStatus) {
      await this.loadPidTransactionStatuses();
    }

    if (this.searchCriteria) {
      this.batchId = this.searchCriteria.batchId;
      this.endDateInput = this.searchCriteria.endDate;
      this.startDateInput = this.searchCriteria.startDate || this.buildDateString(30);
      this.districtCodes = this.searchCriteria.districtCodes;
      this.schoolKeys = this.searchCriteria.schoolKeys;
      this.personTypeCodes = this.searchCriteria.personTypeCodes;
      this.statuses = this.searchCriteria.statuses;
    } else if (this.contextMode) {
      this.personTypeCodes = this.defaultPersonType
        && this.activePersonTypes.some(personType => personType.code === this.defaultPersonType)
        ? [this.defaultPersonType] : [this.activePersonTypes[0].code];
    }

    this.initializing = false;
  },
  data() {
    return {
      batchId: '',
      messages: [],
      startDateInput: this.buildDateString(30),
      endDateInput: '',
      hasErrorStartDate: false,
      hasErrorEndDate: false,
      hasPersonTypeError: false,
      msgPersonType: '',
      msgStartDate: '',
      msgEndDate: '',
      districtCodes: [],
      schoolKeys: [],
      personTypeCodes: [],
      statuses: [],
      initializing: true,
      cancelStatuses: [],
      hasErrorBatchId: false,
      msgBatchId: '',
    };
  },
  methods: {
    async loadPidTransactionStatuses() {
      const result = await axios.get(`trans-groups/${this.statusCode}`);

      if (result && result.data && result.data.list) {
        this.cancelStatuses = result.data.list.map((el) => {
          const { description, prefix } = el;
          return { ...el, ...{ description: prefix ? `${prefix} - ${description}` : description } };
        });
      } else {
        this.cancelStatuses = [];
      }
    },
    search() {
      this.checkForm();
      if (!this.hasErrorStartDate && !this.hasErrorEndDate
        && !this.hasPersonTypeError && !this.hasErrorBatchId) {
        this.searchCriteriaChanged();
        this.$emit('search');
        this.hideNotification();
      } else {
        this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
      }
    },
    personTypesChanged(personTypes) {
      this.personTypeCodes = personTypes.map(pt => pt.code);
      this.validateField('personType');
    },
    districtsChanged(districts) {
      this.districtCodes = districts.map(d => d.code);
      this.handleSpecificLocation();
    },
    schoolsChanged(schools) {
      this.schoolKeys = schools.map(s => `${s.districtCode}-${s.code}`);
      this.handleSpecificLocation();
    },
    handleSpecificLocation() {
      const districts = this.$refs.selDistricts.values;
      const schools = this.$refs.selSchools.values;
      const multiple = {
        code: 'Multiple',
        name: 'Multiple',
      };
      let specificLocation = null;

      if ((districts && districts.length > 1) || (schools && schools.length > 1)) {
        specificLocation = {
          district: multiple,
          school: multiple,
        };
      } else {
        if (districts && districts.length === 1) {
          const district = districts[0];
          specificLocation = {
            district: {
              code: district.code,
              name: district.location.name,
            },
          };
        }

        if (schools && schools.length === 1) {
          const school = schools[0];

          if (schools.length === 1) {
            specificLocation = {
              ...specificLocation,
              school: {
                code: school.code,
                districtCode: school.districtCode,
                name: school.location.name,
              },
            };

            if (!districts || districts.length === 0) {
              specificLocation = {
                ...specificLocation,
                district: {
                  code: school.districtCode,
                  name: school.districtLocation.name,
                },
              };
            }
          }
        }
      }

      this.$emit('specificLocationChanged', specificLocation);
    },
    checkForm() {
      this.validateField('startDate');
      this.validateField('endDate');
      this.validateField('personType');
      this.validateField('batchId');
    },
    validateField(field) {
      if (field === 'personType' && this.contextMode) {
        if (!this.personTypeCodes || this.personTypeCodes.length === 0) {
          this.hasPersonTypeError = true;
          this.msgPersonType = 'Person Type is required.';
        } else {
          this.hasPersonTypeError = false;
          this.msgPersonType = null;
        }
        return;
      }

      if (field === 'startDate') {
        if (!this.startDateInput) {
          this.hasErrorStartDate = true;
          this.msgStartDate = 'Start Date is required.';
        } else if (!this.isValidDateFormat(this.startDateInput, 'YYYY-MM-DD')) {
          this.hasErrorStartDate = true;
          this.msgStartDate = 'Start Date is invalid.';
        } else if (this.endDateInput && moment(this.startDateInput).isAfter(this.endDateInput)) {
          this.hasErrorStartDate = true;
          this.msgStartDate = 'Start Date cannot be after End Date.';
        } else {
          const today = new Date().getTime();
          if (moment(this.startDateInput).isAfter(today)) {
            this.hasErrorStartDate = true;
            this.msgStartDate = 'Start Date cannot be future date.';
          } else {
            this.hasErrorStartDate = false;
            this.msgStartDate = null;
          }
        }
        return;
      }

      if (field === 'endDate') {
        if (this.endDateInput
            && !this.isValidDateFormat(this.endDateInput, 'YYYY-MM-DD')) {
          this.hasErrorEndDate = true;
          this.msgEndDate = 'End Date is invalid.';
        } else if (moment(this.startDateInput).isAfter(this.endDateInput)) {
          this.hasErrorEndDate = true;
          this.msgEndDate = 'End Date cannot be before Start Date.';
        } else {
          const today = new Date().getTime();
          if (moment(this.endDateInput).isAfter(today)) {
            this.hasErrorEndDate = true;
            this.msgEndDate = 'End Date cannot be future date.';
          } else {
            this.hasErrorEndDate = false;
            this.msgEndDate = null;
          }
        }
      }

      if (field === 'batchId' && this.hasBatchId) {
        if (!this.batchId || /^[0-9]*$/.test(this.batchId)) {
          this.hasErrorBatchId = false;
          this.msgBatchId = null;
        } else {
          this.hasErrorBatchId = true;
          this.msgBatchId = 'Batch ID is invalid';
        }
      }
    },
    reset() {
      this.$refs.selDistricts.clear();
      this.$refs.selPersonTypes.clear();
      this.$refs.selSchools.clear();

      if (this.hasStatus) {
        this.$refs.selStatuses.clear();
      }

      this.startDateInput = this.buildDateString(30);
      this.endDateInput = '';
      this.districtCodes = [];
      this.schoolKeys = [];
      this.statuses = [];
      this.batchId = '';

      // set defaults
      if (this.contextMode) {
        this.personTypeCodes = this.defaultPersonType
          && this.activePersonTypes.some(personType => personType.code === this.defaultPersonType)
          ? [this.defaultPersonType] : [this.activePersonTypes[0].code];
        this.$refs.selPersonTypes
          .pushValues(this.activePersonTypes.filter(ap => this.personTypeCodes.includes(ap.code)));
      } else {
        this.personTypeCodes = [];
      }

      this.clearErrors();
      this.searchCriteriaChanged();
      this.$emit('reset');
    },
    searchCriteriaChanged() {
      this.$emit('searchCriteriaChanged', {
        batchId: this.batchId,
        districtCodes: this.districtCodes,
        endDate: this.endDateInput,
        personTypeCodes: this.personTypeCodes,
        schoolKeys: this.schoolKeys,
        startDate: this.startDateInput,
        statuses: this.statuses,
      });
    },
    clearErrors() {
      this.hideNotification();
      this.hasErrorStartDate = false;
      this.hasErrorEndDate = false;
      this.msgEndDate = '';
      this.msgStartDate = '';
      this.hasErrorBatchId = false;
      this.hasPersonTypeError = false;
      this.msgPersonType = '';
      this.msgBatchId = '';
    },
  },
  computed: {
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    contextMode() {
      const identityControl = this.properties['IDENTITY_CONTROL-LIFETIME_IDS'];
      return JSON.parse(identityControl.toString().toLowerCase()) === false;
    },
    getDistrictParams() {
      return {
        districtCode: this.districtCodes,
        active: false,
      };
    },
    // eslint-disable-next-line vue/no-async-in-computed-properties
    async schoolCodesPromises() {
      if (!this.$route.query.schoolKeys) {
        return [];
      }

      const promises = this.$route.query.schoolKeys.split(',').map((s) => {
        const [districtCode, code] = s.split('-');
        return axios.get(`districts/${districtCode}/schools/${code}`);
      });
      // eslint-disable-next-line vue/no-async-in-computed-properties
      const responses = await Promise.all(promises);
      return responses.map(r => r.data);
    },
    labels() {
      return {
        district: this.getFieldLabel(`PER:${this.defaultPersonType}:ASSIGN:DISTRICT_CODE`),
        school: this.getFieldLabel(`PER:${this.defaultPersonType}:ASSIGN:SCHOOL_CODE`),
      };
    },
    districtsApiQueryParams() {
      return {
        active: false,
      };
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "@/sass/custom.sass"
  @import "~bulma/sass/utilities/mixins"

  ul li + li
      margin-top: 0

  /deep/ .multi-dropdown-columns.columns
    display: flex
    flex-wrap: wrap
    .column
      width: fit-content
      flex: 1 0 auto
    @media screen and (max-width: 70em)
      .column
        flex: 1 0 100%
        .field
          border-right: none !important
    .field
      .select
        width: 100%
        select
          width: 100%
    .column
      padding-right: 0
      .field
        padding-right: $column-gap
        border-right: 1px dashed #ccc
        @include until($tablet)
          border-right: none
    .column:last-child
      .field
        border-right: none
</style>
